import { Form } from "antd/es";
import { ColumnType } from "antd/es/table";
import _ from "lodash";
import React, { PureComponent } from "react";
import withCommonEvents from "../../../../shared/hoc/with-common-events";
import { isExplicitFieldWrite } from "../../../../shared/utilty/row-editor-field-writes";
import { CommonProps } from "../../common/common-props";

export interface TableDataProps extends ColumnType<any> {
  rowData?: any;
  children?: React.ReactNode;
  isEditableColumn?: boolean;
  isEditMode?: boolean;
  visibilityCondition?: (rowData: object) => boolean;
  editabilityCondition?: (rowData: object) => boolean;
}

declare let window: any;
interface KTableDataState {}

class TableData<P, S> extends PureComponent<TableDataProps & P & CommonProps, KTableDataState & S> {
  constructor(props: TableDataProps & P & CommonProps) {
    super(props);
  }

  static dataFieldKey: RegExp = /\[datafield:((?:(?!\[datafield:).)*)\]/;

  static oldValue: RegExp = /\[datafield:((?:(?!\[datafield:).)*)\]/g;

  private static camelCaseCache = new Map<string, string>();
  private static camelCaseRegex = /^([A-Z])|[\s-](\w)/g;
  private static pathFieldsCache = new Map<string, string[]>();
  private static pathFieldsRawCache = new Map<string, string[]>();

  static toCamelCase = (str: string): string => {
    let cached = TableData.camelCaseCache.get(str);
    if (cached !== undefined) return cached;

    cached = str.replace(TableData.camelCaseRegex, (match, p1, p2) => {
      if (p2) {
        return p2.toUpperCase();
      }

      return p1.toLowerCase();
    });
    TableData.camelCaseCache.set(str, cached);
    return cached;
  };

  // Raw (non-camelCased) path segments; getValueFromPath falls back to these.
  private static resolvePathFieldsRaw = (path: string): string[] => {
    let cached = TableData.pathFieldsRawCache.get(path);
    if (cached) return cached;

    cached = path.split(".").filter((field: string, index: number, array: string[]) => {
      const theFieldIndexContaingSelected = array.findIndex((x) => x.includes("[selected]"));

      if (theFieldIndexContaingSelected > -1) {
        return index > theFieldIndexContaingSelected;
      }

      return true;
    });
    TableData.pathFieldsRawCache.set(path, cached);
    return cached;
  };

  private static resolvePathFields = (path: string): string[] => {
    let cached = TableData.pathFieldsCache.get(path);
    if (cached) return cached;

    cached = TableData.resolvePathFieldsRaw(path).map((x) => TableData.toCamelCase(x));
    TableData.pathFieldsCache.set(path, cached);
    return cached;
  };

  static getValueFromPath = (data: object, path: string, index: number) => {
    // Example path: "Users[first].Name
    const fields = TableData.resolvePathFields(path);
    const rawFields = TableData.resolvePathFieldsRaw(path);

    const value = fields.reduce((prev: any, curr: string, i: number) => {
      if (!prev) return "";

      const raw: string = rawFields[i] ?? curr;

      // camelCase key first (existing behavior), then raw key so PascalCase
      // datasource fields (e.g. "Photo") resolve in the carousel path too.
      const twoKeyGet = (camelKey: string, rawKey: string) => {
        const camelValue = prev?.[camelKey];
        if (camelValue !== undefined && camelValue !== null) return camelValue;
        return prev?.[rawKey];
      };

      if (curr.endsWith("[first]")) {
        return twoKeyGet(curr.replace("[first]", ""), raw.replace("[first]", ""))?.at?.(0);
      } else if (curr.endsWith("[last]")) {
        return twoKeyGet(curr.replace("[last]", ""), raw.replace("[last]", ""))?.at?.(-1);
      } else if (curr.endsWith("[selected]")) {
        return twoKeyGet(curr.replace("[selected]", ""), raw.replace("[selected]", ""))?.at?.(index);
      }

      return twoKeyGet(curr, raw);
    }, data);

    return value;
  };

  static getIsDataRepeater = (child: any): boolean => {
    if (!child) return false;

    if (child?.props?.dataSource && child.props.dataSource?.length > 0) {
      return true;
    }

    return false;
  };

  // antd's Form.Item (rc-field-form `Field`) always overwrites its child's own
  // value: getControlled() builds `{ ...childProps, ...getValueProps(formValue) }`
  // and clones the child with it, so the form-store value for `name` wins. Inside
  // a DataRepeater row that is wrong — the per-row value substituted from
  // `[datafield:X]` below is replaced by the screen-level, row-agnostic form
  // field, which for repeater children is never seeded and therefore resolves to
  // undefined. Components wrapped with a Form.Item (Image, and any other
  // `wrapWithFormItem` element) then render their empty state — for Image the
  // "no image" placeholder — in every row of a Carousel / HorizontalGrid /
  // GalleryView / KanbanBoard (KOA-18127). Table columns escaped it only because
  // KuikaEditableCell (KOA-13928) sits in between and re-injects the row value.
  //
  // An empty `getValueProps` result makes rc-field-form contribute no value prop
  // at all, so the row-substituted value survives. The change trigger is added
  // separately by getControlled(), so edits still reach the form store and
  // action bindings reading `form.getFieldValue(...)` keep working.
  private static readonly keepRowSubstitutedValue = (): object => ({});

  // A marked row editor (codegen's `data-row-editor`, KOA-18430) is row-owned for the
  // same reason — one field cannot address one row — but SetValueOf writes that same
  // field, and setting every row's editor at once is a supported authoring pattern.
  // rc-field-form cannot tell the two writes apart, so setValueOf marks a paint
  // (row-editor-field-writes) and the field is shown here only while that paint is live.
  // BaseTable drops the mark on the first keystroke, so typing hands ownership straight
  // back to the row. The mark deliberately does not pin the painted value: codegen gives a
  // bound editor an onChange that rewrites this same field with the input's DOM value, so
  // the number SetValueOf wrote returns as a string and pinning it collapsed the paint.
  private static readonly explicitWriteValueProps: Map<string, (val: unknown) => object> = new Map<
    string,
    (val: unknown) => object
  >();

  private static honourExplicitWriteOnly = (fieldName: string, valuePropName: string): ((val: unknown) => object) => {
    const cacheKey: string = `${fieldName}::${valuePropName}`;
    const cached: ((val: unknown) => object) | undefined = TableData.explicitWriteValueProps.get(cacheKey);
    if (cached !== undefined) {
      return cached;
    }

    // One stable identity per field+prop: this runs per cell per row, and a fresh
    // closure would make every row's Form.Item look like changed props.
    const resolver: (val: unknown) => object = (val: unknown): object =>
      isExplicitFieldWrite(fieldName, val) ? { [valuePropName]: val } : {};
    TableData.explicitWriteValueProps.set(cacheKey, resolver);
    return resolver;
  };

  // A cell value can also be static — a Symbol Picker → UploadedImages GUID, a
  // literal URL — with no `[datafield:X]` token for the scan below to find. Those
  // are emitted inline on the element and, like the row-bound ones, never reach
  // the form store: codegen's IsFormItemElement() returns false for every
  // table/repeater child, so fillFormInitValues() skips them. The named
  // Form.Item still injects, so the child received `undefined` and an <Image>
  // rendered the "no image" placeholder (KOA-18281).
  //
  // Here the form field cannot simply be ignored the way keepRowSubstitutedValue
  // ignores it: nothing else is row-specific, so a runtime write — SetValueOf,
  // setFieldsValue — must still win. Gate on whether the field carries a value
  // at all. Unseeded (`undefined`) yields to the child; anything else, `null`
  // included, is a deliberate write and keeps rc-field-form's own semantics.
  private static readonly deferToFormCache: Map<string, (val: unknown) => object> = new Map<
    string,
    (val: unknown) => object
  >();

  private static deferToFormOnlyWhenSeeded = (valuePropName: string): ((val: unknown) => object) => {
    const cached: ((val: unknown) => object) | undefined = TableData.deferToFormCache.get(valuePropName);
    if (cached !== undefined) {
      return cached;
    }

    // One stable identity per prop name: this runs per cell per row, and a fresh
    // closure would make every row's Form.Item look like changed props.
    const deferred: (val: unknown) => object = (val: unknown): object =>
      val === undefined ? {} : { [valuePropName]: val };
    TableData.deferToFormCache.set(valuePropName, deferred);
    return deferred;
  };

  // The scan below runs per cell per row, but its input is the immutable
  // pre-substitution element tree, which every row of a repeater shares. Cache
  // on node identity so a 1000-row table walks each cell template once. Keyed by
  // the prop name being scanned, since a cell can be reached with either.
  private static rowBoundValuePropCache = new Map<string, WeakMap<object, string | null>>();

  /**
   * The row field the cell's value is bound to (`[datafield:X]` → `"X"`), or null
   * when it carries no such token.
   *
   * `hasRowBoundValueProp` below is the yes/no form of the same question, and both
   * share this one cached walk. The name itself is needed on the blur path: an
   * editor the user never typed into must hand the shared form field back its own
   * row's value, and that value can only be looked up in the row record by name
   * (KOA-17432).
   *
   * Two props count as that value. `value` (or the Form.Item's `valuePropName`)
   * is the obvious one. `defaultValue` is the same value wearing another name:
   * for a data-entry component inside a TableTemplateColumn, codegen routes
   * edits through an `onChange` that writes the shared form field and emits the
   * row token on `defaultValue` instead of `value` (KOA-15389). Scanning only
   * `valuePropName` missed those cells, so they fell to the seeded-ness branch
   * and the first keystroke in any row overwrote every row (KOA-18430).
   *
   * Still deliberately scoped to the value: a cell bound only through some other
   * prop (`tooltip`, `visExField1`, …) has no row-bound value of its own, so the
   * form field keeps ownership of it exactly as before — and a cell with no token
   * at all stays on `deferToFormOnlyWhenSeeded`, so an explicit SetValueOf still
   * wins there (KOA-18281). Scanned on the pre-substitution element tree, so the
   * tokens are still intact.
   */
  static rowBoundValueField = (node: React.ReactNode, valuePropName: string): string | null => {
    let cache: WeakMap<object, string | null> | undefined = TableData.rowBoundValuePropCache.get(valuePropName);
    if (!cache) {
      cache = new WeakMap<object, string | null>();
      TableData.rowBoundValuePropCache.set(valuePropName, cache);
    }

    const cacheKey: object | null = node !== null && typeof node === "object" ? (node as object) : null;
    if (cacheKey !== null && cache.has(cacheKey)) {
      return cache.get(cacheKey) ?? null;
    }

    let found: string | null = null;

    React.Children.forEach(node, (child: React.ReactNode) => {
      if (found !== null || !React.isValidElement(child)) {
        return;
      }

      const boundValues: unknown[] = [child.props[valuePropName], child.props.defaultValue];
      for (const boundValue of boundValues) {
        if (typeof boundValue !== "string") {
          continue;
        }
        const match: RegExpMatchArray | null = boundValue.match(TableData.dataFieldKey);
        if (match !== null) {
          // `[datafield:]` — a bound-but-unnamed value — matches with an empty group. It
          // still counts as row-bound for hasRowBoundValueProp; it just names no field for
          // the blur path to look up.
          found = match[1] ?? "";
          break;
        }
      }

      if (found === null) {
        found = TableData.rowBoundValueField(child.props.children, valuePropName);
      }
    });

    if (cacheKey !== null) {
      cache.set(cacheKey, found);
    }

    return found;
  };

  static hasRowBoundValueProp = (node: React.ReactNode, valuePropName: string): boolean =>
    TableData.rowBoundValueField(node, valuePropName) !== null;

  static setDataFieldValues = (
    children: any,
    rowData?: any,
    rowIndex?: number,
    _visibilityCondition?: (rowData: object) => boolean,
    _editabilityCondition?: (rowData: object) => boolean
  ): React.ReactNode[] => {
    if (!children) return [];

    if (window.kuika.isDesignTime) {
      return children;
    }

    return React.Children.map(children, (child, childIndex) => {
      const props: Record<string, any> = {};
      let manipulatedChildren: React.ReactNode[] = [];

      if (child?.props) {
        const visibilityCondition = child.props?.visibilityCondition || _visibilityCondition;

        if (visibilityCondition) {
          if (child?.props?.visibilityByAuthorization === "hidden") {
            props.visibility = "hidden";
          } else {
            props.visibility = visibilityCondition(rowData) === true ? "visible" : "hidden";
          }
        }

        const editabilityCondition = child.props?.editabilityCondition || _editabilityCondition;

        if (editabilityCondition) {
          if (child?.props?.editabilityByAuthorization === "disabled") {
            props.editability = "disabled";
          } else {
            props.editability = editabilityCondition(rowData) === true ? "enabled" : "disabled";
          }
        }

        Object?.keys(child.props)?.forEach((key) => {
          if (typeof child.props[key] === "string") {
            if (child.props[key]?.toString().indexOf("datafield:") > -1) {
              const matchResult = child.props[key]?.toString().match(TableData.dataFieldKey);

              if (rowData && matchResult && matchResult?.length > 0) {
                props[`${key}Original`] = child.props[key];

                const dataField = matchResult[1];
                let newValue = this.getValueFromPath(rowData, dataField, rowIndex) ?? "";

                if (Array.isArray(newValue) || _.isObject(newValue)) {
                  props[key] = newValue;
                } else {
                  props[key] = child.props[key]?.toString().replace(TableData.oldValue, newValue);
                }

                if (
                  !window ||
                  !window.kuika ||
                  (window.kuika?.dashboardState !== 1 && window.kuika?.dashboardState !== 17)
                ) {
                  if ((key === "dataSource" && props[key] == "") || props[key] == "null" || props[key] == "undefined") {
                    props[key] = [];
                  } else if (props[key] == "" || props[key] == "null" || props[key] == "undefined") {
                    props[key] = " ";
                  }
                }
              }
            }

            if (key === "children") {
              manipulatedChildren.push(props.children);
            }
          } else if (key === "children") {
            if (TableData.getIsDataRepeater(child)) {
              return;
            }

            manipulatedChildren = TableData.setDataFieldValues(child.props.children, rowData, rowIndex);
          }

          if (!props[key]) {
            props[key] = child.props[key];
          }
        });
      }

      props.rowIndex = rowIndex;
      props.isTableData = true;

      // Take the cell's value back from the (never-seeded) screen-level form
      // field. A row-bound cell keeps its row value unconditionally — one field
      // cannot address one row, so a single write must not blank every row
      // (KOA-18127). A cell codegen marked as a row editor is row-owned for the
      // same reason even though it carries no token: an unbound data-entry
      // component has no row value to substitute, so the only sane owner of what
      // the user typed is that row's own input (KOA-18430). Every other cell
      // keeps its own value only while the field is unseeded, so an explicit
      // write still wins (KOA-18281).
      if (child?.type === Form.Item && child.props?.name !== undefined && child.props?.getValueProps === undefined) {
        const valuePropName: string = child.props?.valuePropName ?? "value";
        // Codegen writes the cell's own form key here, so the attribute doubles as the
        // marker and as what BaseTable's focus capture needs (KOA-17432).
        const rowEditorField: unknown = child.props?.["data-row-editor"];
        const isMarkedRowEditor: boolean = typeof rowEditorField === "string" && rowEditorField.length > 0;
        if (isMarkedRowEditor) {
          props.getValueProps = TableData.honourExplicitWriteOnly(rowEditorField as string, valuePropName);
          // Leaving a marked editor without typing must hand the shared field back this
          // row's own value, and BaseTable can only look that up by name (KOA-17432).
          const boundField: string | null = TableData.rowBoundValueField(child.props?.children, valuePropName);
          if (boundField) {
            props["data-row-editor-field"] = boundField;
          }
        } else if (TableData.hasRowBoundValueProp(child.props?.children, valuePropName) === true) {
          props.getValueProps = TableData.keepRowSubstitutedValue;
        } else {
          props.getValueProps = TableData.deferToFormOnlyWhenSeeded(valuePropName);
        }
      }

      if (manipulatedChildren?.length > 0) {
        // antd Form.Item injects value/onChange into its child only when
        // `children` is a single React element; a length-1 array silently
        // disables the injection, so editable-cell picks never reach the form
        // and action bindings read empty values. Preserve Form.Item's
        // single-child contract by unwrapping the singleton.
        if (child.type === Form.Item && manipulatedChildren.length === 1) {
          return React.cloneElement(child, props, manipulatedChildren[0]);
        }
        return React.cloneElement(child, props, manipulatedChildren);
      }

      return React.cloneElement(child, props, child.props.children);
    });
  };

  render = () => {
    if (this.props.visibility === "hidden") return <></>;
    return TableData.setDataFieldValues(
      this.props.children,
      this.props.rowData,
      this.props.rowIndex,
      this.props.visibilityCondition,
      this.props.editabilityCondition
    );
  };
}

const tableData = withCommonEvents<TableDataProps & CommonProps, KTableDataState>(TableData);
export { tableData as TableData };
// withCommonEvents does not hoist statics; tests exercise
// TableData.setDataFieldValues through this named class export.
export { TableData as TableDataClass };
